import React from "react";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import {
  useListContext,
  Filter,
  TextInput,
  Datagrid,
  TextField,
  FunctionField,
  Record,
  TopToolbar,
  sanitizeListRestProps,
  CreateButton,
  Responsive,
  SimpleList,
} from "react-admin";
import { EldDeviceListDto } from "../../../common/data/service-proxies";
import { Box, useTheme, Theme, Typography, Tooltip } from "@material-ui/core";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useAppData } from "../../../common/hooks/useAppData";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { useFieldPermissions } from "../../../common/hooks/useFieldPermissions";
import { protectedEntities } from "../../../common/auth/permissions";
import { FieldPermissionsContext } from "../../common/field-permissions/FieldPermissionsContext";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import { DeleteRecordMenuItem } from "../../common/data-grid-fields/DeleteRecordMenuItem";

const ListActions = (props: any) => {
  const { className, exporter, filters, maxResults, ...rest } = props;
  const { basePath } = useListContext();
  const permissions = useActionPermissions();
  const hasOwnElds = useAppData((x) => x.tenant?.hasOwnElds) ?? false;
  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {permissions &&
        permissions[permissionNames.admin.elds.main] &&
        permissions[permissionNames.admin.elds.create] &&
        hasOwnElds && <CreateButton basePath={basePath} />}
    </TopToolbar>
  );
};

const ListFilter = (props: any) => {
  useSearchFields(props, ["serialNumber", "vehicleName"]);
  useResetListSearchField(props);
  const { loading } = useListContext();
  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="companies-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="companies-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
    </Filter>
  );
};

export const EldsListView = (props: any) => {
  const theme: Theme = useTheme();
  const permissions = useActionPermissions();
  const fieldPermissions = useFieldPermissions(
    protectedEntities.EldDevice,
    (x) => x.list
  );

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}>
        <Tooltip title="Email">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}>
            <LocalShippingOutlinedIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="vehicleName">
              <TextField
                source="vehicleName"
                label="resources.admin-elds.labels.vehicleName"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <FunctionField<EldDeviceListDto & Record>
          source="status"
          headerClassName="centered-content"
          label="resources.common.labels.active"
          render={(record) => (
            <Box className="centered-content">
              {record?.status === "Active" ? (
                <CheckBoxIcon
                  className={record.status}
                  style={{
                    color: theme.palette.success.main,
                  }}
                />
              ) : (
                <IndeterminateCheckBoxIcon
                  className={record?.status}
                  style={{
                    color: theme.palette.error.main,
                  }}
                />
              )}
            </Box>
          )}
        />
      </div>
    );
  };

  const RenderSecondaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          marginTop: "14px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            flexWrap: "wrap",
            alignItems: "center",
          }}>
          <Typography
            variant="body2"
            style={{ fontWeight: "600", color: theme.palette.text.secondary }}>
            Serial number:
          </Typography>
          <ProtectedElement source="serialNumber">
            <TextField
              source="serialNumber"
              label="resources.admin-elds.labels.serialNumber"
            />
          </ProtectedElement>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            flexWrap: "wrap",
            alignItems: "center",
          }}>
          <Typography
            variant="body2"
            style={{ fontWeight: "600", color: theme.palette.text.secondary }}>
            Vendor:
          </Typography>
          <ProtectedElement source="vendor">
            <TextField
              source="vendor"
              label="resources.admin-elds.labels.vendor"
            />
          </ProtectedElement>
        </div>
      </div>
    );
  };

  return (
    <div className="content-container">
      <FieldPermissionsContext.Provider value={fieldPermissions}>
        <DefaultProLogsList
          title="resources.admin-elds.name"
          bulkActionButtons={false}
          actions={<ListActions />}
          filters={<ListFilter />}
          sort={{ field: "serialNumber", order: "ASC" }}
          {...props}>
          <Responsive
            small={
              <SimpleList
                className="simple-list-wrapper"
                linkType={false}
                primaryText={(record) => (
                  <RenderPrimaryTextForSimpleList record={record} />
                )}
                secondaryText={(record) => (
                  <RenderSecondaryTextForSimpleList record={record} />
                )}
                rightIcon={(record) => {
                  return (
                    <ActionsField
                      {...props}
                      record={record}
                      editable={(x) =>
                        permissions &&
                        permissions[permissionNames.admin.elds.main] &&
                        permissions[permissionNames.admin.elds.update] &&
                        !x.assignedByMaster
                      }
                      canActivateOrDeactivate={(x) =>
                        permissions &&
                        permissions[permissionNames.admin.elds.main] &&
                        permissions[permissionNames.admin.elds.update] &&
                        !x.assignedByMaster
                      }
                    />
                  );
                }}
              />
            }
            medium={
              <Datagrid>
                <ProtectedElement source="serialNumber">
                  <TextField
                    source="serialNumber"
                    label="resources.admin-elds.labels.serialNumber"
                  />
                </ProtectedElement>
                <ProtectedElement source="vendor">
                  <TextField
                    source="vendor"
                    label="resources.admin-elds.labels.vendor"
                  />
                </ProtectedElement>
                <ProtectedElement source="vehicleName">
                  <TextField
                    source="vehicleName"
                    label="resources.admin-elds.labels.vehicleName"
                  />
                </ProtectedElement>
                <FunctionField<EldDeviceListDto & Record>
                  source="status"
                  headerClassName="centered-content"
                  label="resources.common.labels.active"
                  render={(record) => (
                    <Box className="centered-content">
                      {record?.status === "Active" ? (
                        <CheckBoxIcon
                          className={record.status}
                          style={{
                            color: theme.palette.success.main,
                          }}
                        />
                      ) : (
                        <IndeterminateCheckBoxIcon
                          className={record?.status}
                          style={{
                            color: theme.palette.error.main,
                          }}
                        />
                      )}
                    </Box>
                  )}
                />
                <ActionsField
                  editable={(x) =>
                    permissions &&
                    permissions[permissionNames.admin.elds.main] &&
                    permissions[permissionNames.admin.elds.update] &&
                    !x.assignedByMaster
                  }
                  canActivateOrDeactivate={(x) =>
                    permissions &&
                    permissions[permissionNames.admin.elds.main] &&
                    permissions[permissionNames.admin.elds.update] &&
                    !x.assignedByMaster
                  }>
                  <DeleteRecordMenuItem
                    hidden={(x) =>
                      !!x.assignedByMaster || !!x.vehicleName?.trim()?.length
                    }
                  />
                </ActionsField>
              </Datagrid>
            }
          />
        </DefaultProLogsList>
      </FieldPermissionsContext.Provider>
    </div>
  );
};
